<script setup>
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { applyStyleToVariable } from "@/utils/editorStyling";
import axios from "axios";
import scrollToElement from "@/utils/scrollToElement";
import { useIsMobile } from "@/utils/checkMobile";
import { formatIsoDate } from "@/utils/formatDate";

const { currentDocument, meta, requiresPayment } = defineProps([
  "currentDocument",
  "meta",
  "requiresPayment",
]);

const emit = defineEmits(["completeWizard", "next", "noActionTaken", "paymentRequired"]);
const store = useStore();
const { t } = useI18n();
const isMobile = useIsMobile();

// Also check for tablets up to 1024px for navigation buttons
const isTabletOrMobile = ref(window.innerWidth <= 1024);
const updateIsTabletOrMobile = () => {
  isTabletOrMobile.value = window.innerWidth <= 1024;
};

onMounted(() => {
  window.addEventListener("resize", updateIsTabletOrMobile);
  updateIsTabletOrMobile();
});

onUnmounted(() => {
  window.removeEventListener("resize", updateIsTabletOrMobile);
});

const isLoading = ref(false);
const errorMessage = ref("");
const variablesNodes = [];
const allVariables = reactive(store.state.editor.availableVariables);
const availableVariables = ref([]);
const dateMenu = ref(false);
const dateMenuMobile = ref(false);
const currentVariableIndex = computed(
  () => store.state.editor.variables_wizard.currentVariableIndex,
);
const currentVariable = computed(() => {
  errorMessage.value = "";
  return availableVariables.value[currentVariableIndex.value];
});

const isVariableEmpty = computed(() => {
  const value = variableValue.value;

  // Check if value is truly empty
  const isEmpty = !value ||
         value === '' ||
         value === null ||
         value === undefined ||
         (typeof value === 'string' && value.trim() === '');

  return isEmpty;
});

const progress = computed(
  () => ((currentVariableIndex.value + 1) / availableVariables.value.length) * 100,
);

const setVariablesFromApi = async () => {
  const preparedVariables = meta.variables.map((variable) => ({
    id: variable.id,
    value: variable.answer,
  }));

  store.commit("editor/setVariables", preparedVariables);

  // Calculate the index based on filled variables
  // This represents the first empty variable that needs to be filled
  const calculatedIndex = preparedVariables.filter((variable) => variable.value).length || 0;

  // Check if there's a stored currentVariableIndex in Vuex (persisted from previous session)
  const storedIndex = store.state.editor.variables_wizard.currentVariableIndex;

  // Use the stored index ONLY if it's pointing to an unfilled variable
  // This preserves user position when they were in the middle of filling a variable
  if (
    storedIndex !== null &&
    storedIndex !== undefined &&
    storedIndex >= 0 &&
    storedIndex < preparedVariables.length &&
    !preparedVariables[storedIndex]?.value  // Variable at stored index is NOT filled
  ) {
    // Use the stored index - user is returning to a variable they were working on
    store.commit("editor/setCurrentVariableIndex", storedIndex);
  } else {
    // Use calculated index - go to the first unfilled variable
    store.commit("editor/setCurrentVariableIndex", calculatedIndex);
  }
};

const variableValue = computed({
  get() {
    const { answer } = store.state.editor.editableVariable;

    if (currentVariable.value?.type === "date" && answer) {
      // Handle both string and Date object formats
      let dateStr;
      if (typeof answer === 'string') {
        dateStr = answer;
      } else {
        // Use local date components, not UTC
        const year = answer.getFullYear();
        const month = String(answer.getMonth() + 1).padStart(2, '0');
        const day = String(answer.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      }
      const year = dateStr.split("-")[0];
      const isIsoFormat = year.length === 4;

      if (isIsoFormat) {
        return dateStr;
      } else {
        return dateStr.split("-").reverse().join("-");
      }
    } else {
      return store.state.editor.editableVariable.answer;
    }
  },
  set(value) {
    const { id, name, type, description, options, max_options, list_type } = currentVariable.value;
    
    // Convert Date object to local date string if necessary
    let processedValue = value;
    if (currentVariable.value?.type === "date" && value instanceof Date) {
      // Use local date components, not UTC
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      processedValue = `${year}-${month}-${day}`;
    }

    const newVariable = {
      id,
      name,
      type,
      description,
      answer: processedValue,
      value: processedValue,
    };

    if (type === "select") {
      const newValue = Array.isArray(value) ? [...value] : [value];
      const selectedOptions = options.filter((option) => newValue.includes(option));

      newVariable.value = selectedOptions;
      newVariable.answer = selectedOptions;
      newVariable.options = selectedOptions;
      newVariable.max_options = max_options;
      newVariable.list_type = list_type;
    }

    store.commit("editor/updateEditableVariable", newVariable);
    errorMessage.value = "";
  },
});

const handleSelectVariable = () => {
  const isLastItem = currentVariableIndex.value >= availableVariables.value.length;

  if (isLastItem && availableVariables.value.length > 0) {
    applyStyleToVariable(null);
    store.commit("editor/updateEditableVariable", {
      name: "",
      value: "",
      answer: "",
      description: "",
      type: "text",
      id: null,
    });

    emit("completeWizard");
    return;
  }

  const { id, name, type, description, answer } = currentVariable.value || {};

  applyStyleToVariable(id);
  store.commit("editor/updateEditableVariable", {
    id,
    name,
    type,
    description,
    value: store.state.editor.variables[id] || '',
    answer: store.state.editor.variables[id] || '',
  });

  const selectedVariable = variablesNodes.find((variable) => variable.dataset.id === id);

  scrollToElement(selectedVariable);

  // Set focus on the input field after a short delay to ensure DOM is updated
  nextTick(() => {
    const inputField = document.querySelector('.variable-inputs-container .v-text-field input');
    if (inputField && currentVariable.value?.type !== 'select') {
      inputField.focus();
    }
  });
};

const handlePreviousVariable = () => {
  if (currentVariableIndex.value > 0) {
    store.commit("editor/setCurrentVariableIndex", currentVariableIndex.value - 1);
    // Reset the variable value to the saved one
    const prevVariable = availableVariables.value[currentVariableIndex.value - 1];
    variableValue.value = prevVariable?.value || "";
    errorMessage.value = "";
  }
};

const handleSaveVariable = async () => {
  if (requiresPayment) {
    emit("paymentRequired");
    return;
  }

  if (isLoading.value) return;

  if (currentVariable.value.type === "select") {
    if (!variableValue.value?.length) {
      errorMessage.value = t("errors.wizard.min_options");
      return;
    }

    if (currentVariable.value?.max_options < variableValue.value.length) {
      errorMessage.value = `${t("errors.wizard.max_options1")} ${currentVariable.value?.max_options} ${t("errors.wizard.max_options2")}`;
      return;
    }
  }

  if (!variableValue.value) {
    errorMessage.value = t("errors.editor.variable_value_required");
    return;
  }

  isLoading.value = true;
  const { id, type } = currentVariable.value;
  let newVariableValue = variableValue.value;

  if (type === "date") {
    // Handle Date object from v-date-picker
    if (newVariableValue instanceof Date) {
      // Use local date components, not UTC
      const year = newVariableValue.getFullYear();
      const month = String(newVariableValue.getMonth() + 1).padStart(2, '0');
      const day = String(newVariableValue.getDate()).padStart(2, '0');
      newVariableValue = `${year}-${month}-${day}`;
    }
    newVariableValue = formatIsoDate(newVariableValue);
  }

  store.commit("editor/updateVariable", {
    id,
    value: newVariableValue,
    answer: newVariableValue,
  });

  try {
    await axios.patch(`api/documents/${currentDocument.id}/meta/variables/answer`, {
      identifier: id,
      answer: newVariableValue + "",
    });

    store.commit("editor/setCurrentVariableIndex", currentVariableIndex.value + 1);
    handleSelectVariable();

    // Emit next event to trigger history saving
    emit("next");
  } catch (error) {
    useToast().error(t("errors.global.too_many_requests"));
  } finally {
    isLoading.value = false;
  }
};

const handleBack = () => {
  store.commit("editor/setCurrentVariableIndex", currentVariableIndex.value - 1);
  handleSelectVariable();
};

const loadAvailableVariablesFromNodes = () => {
  const newNodes = Array.from(document.querySelectorAll("variable"));
  variablesNodes.push(...newNodes);
  const variablesIds = variablesNodes.map((node) => {
    return node.dataset.id;
  });

  availableVariables.value = allVariables.filter((variable) => variablesIds.includes(variable.id));
};

//check if variables are loaded in the editor
const observeVariables = (callback) => {
  const observer = new MutationObserver((mutations) => {
    if (mutations.length) {
      callback();
      observer.disconnect();
    }
  });
  const config = {
    childList: true,
    subtree: true,
  };
  const targetNode = document.querySelector("#editor");
  if (targetNode) {
    observer.observe(targetNode, config);
  }
};

watch(currentVariable, (newValue) => applyStyleToVariable(newValue?.id), {
  deep: true,
});

const actionTaken = ref(false);
let actionTimer = null;

watch(
  () => currentVariable.value,
  () => {
    actionTaken.value = false;
    clearTimeout(actionTimer);
    actionTimer = setTimeout(() => {
      if (!actionTaken.value) {
        emit("noActionTaken", {
          id: currentVariable.value?.id,
          type: "variable",
        });
      }
    }, 60000 * 7); // 7 minutes
  },
);

onMounted(() => {
  loadAvailableVariablesFromNodes();

  // IMPORTANT: Set variables and restore index BEFORE selecting variable
  // This ensures currentVariableIndex is correct before handleSelectVariable checks it
  setVariablesFromApi();

  handleSelectVariable();

  observeVariables(() => {
    loadAvailableVariablesFromNodes();
    handleSelectVariable();
  });

  store.commit("editor/setWizardCurrentStep", null);
});
</script>

<template>
  <div class="variable-inputs-container">
    <!-- Mobile/Tablet view -->
    <template v-if="isTabletOrMobile">
      <div class="progress-bar">
        <span>
          {{ t("wizard.progress") }}
        </span>
      </div>

      <!-- Progress line without buttons on mobile -->
      <div class="wizard-progress-mobile">
        <v-progress-linear
          v-model="progress"
          :height="18"
          color="#3B82F6"
          bg-color="#F2F2F2"
          bg-opacity="1"
          rounded
        ></v-progress-linear>
      </div>

      <!-- CONTENT -->
      <h3 v-if="currentVariable?.name !== ''">
        {{ currentVariable?.name }}
      </h3>

      <v-card v-if="currentVariable">
        <span class="input-label" v-if="currentVariable?.description">
          {{ currentVariable?.description }}
        </span>
        
        <!-- Date picker for date type -->
        <v-menu
          v-if="currentVariable?.type === 'date'"
          v-model="dateMenuMobile"
          :close-on-content-click="false"
          :close-on-back="true"
          :persistent="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model.trim="variableValue"
              :placeholder="currentVariable?.example"
              variant="outlined"
              readonly
              :error-messages="errorMessage"
              append-inner-icon="mdi-calendar"
              :disabled="isLoading || requiresPayment"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="variableValue"
            @update:model-value="dateMenuMobile = false"
            color="#3B82F6"
            hide-header
            show-adjacent-months
            density="compact"
          ></v-date-picker>
        </v-menu>
        
        <!-- Regular text field for other types -->
        <v-text-field
          v-else-if="currentVariable?.type !== 'select'"
          v-model.trim="variableValue"
          :placeholder="currentVariable?.example"
          variant="outlined"
          :type="currentVariable?.type"
          :error-messages="errorMessage"
          @keydown.enter="handleSaveVariable"
          :disabled="isLoading || requiresPayment"
        ></v-text-field>

        <div class="options-fields" v-if="currentVariable?.type === 'select'">
          <v-checkbox
            v-for="option in currentVariable?.options"
            v-model="variableValue"
            :label="option"
            :value="option"
            hide-details
            density="compact"
          ></v-checkbox>
        </div>
        <p v-if="currentVariable?.type === 'select' && errorMessage" class="max-options-label">
          {{ errorMessage }}
        </p>
      </v-card>

      <!-- Navigation buttons for mobile -->
      <div class="mobile-navigation-buttons">
        <v-btn
          @click="handlePreviousVariable"
          :disabled="currentVariableIndex === 0"
          variant="outlined"
          class="mobile-nav-button secondary"
        >
          <v-icon size="18">mdi-chevron-left</v-icon>
          {{ t("common.back") }}
        </v-btn>

        <v-btn
          :disabled="!variableValue || currentVariableIndex >= availableVariables?.length || isLoading"
          @click="handleSaveVariable"
          class="mobile-nav-button primary"
          variant="flat"
          :loading="isLoading"
        >
          {{ currentVariableIndex < availableVariables?.length - 1 ? t("common.next") : t("common.save") }}
          <v-icon size="18">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </template>

    <!-- Desktop view -->
    <template v-else>
      <!-- Progress bar moved to be inline without "Postęp" label -->
      <v-progress-linear
        v-model="progress"
        :height="10"
        color="#3B82F6"
        bg-color="#F2F2F2"
        bg-opacity="1"
        class="mb-3"
      ></v-progress-linear>

      <h3 v-if="currentVariable?.name !== ''">
        {{ currentVariable?.name }}
      </h3>

      <v-card v-if="currentVariable" class="overflow-y-auto">
        <span class="input-label" v-if="currentVariable?.description">
          {{ currentVariable?.description }}
        </span>
        
        <!-- Date picker for date type -->
        <v-menu
          v-if="currentVariable?.type === 'date'"
          v-model="dateMenu"
          :close-on-content-click="false"
          :close-on-back="true"
          :persistent="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-bind="props"
              v-model.trim="variableValue"
              :placeholder="currentVariable?.example"
              variant="outlined"
              readonly
              :error-messages="errorMessage"
              append-inner-icon="mdi-calendar"
              :disabled="isLoading || requiresPayment"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="variableValue"
            @update:model-value="dateMenu = false"
            color="#3B82F6"
            hide-header
            show-adjacent-months
            density="compact"
          ></v-date-picker>
        </v-menu>
        
        <!-- Regular text field for other types -->
        <v-text-field
          v-else-if="currentVariable?.type !== 'select'"
          v-model.trim="variableValue"
          :placeholder="currentVariable?.example"
          variant="outlined"
          :type="currentVariable?.type"
          :error-messages="errorMessage"
          @keydown.enter="handleSaveVariable"
          :disabled="isLoading || requiresPayment"
        ></v-text-field>

        <div class="options-fields" v-if="currentVariable?.type === 'select'">
          <v-checkbox
            v-for="option in currentVariable?.options"
            v-model="variableValue"
            :label="option"
            :value="option"
            hide-details
            density="compact"
          ></v-checkbox>
        </div>
        <p v-if="currentVariable?.type === 'select' && errorMessage" class="max-options-label">
          {{ errorMessage }}
        </p>
      </v-card>

      <div class="wizard-buttons">
        <v-btn
          v-if="currentVariableIndex > 0"
          @click="handleBack"
          class="back-button"
          variant="flat"
        >
          <img width="7" src="/src/assets/icons/arrow-back.svg" class="mr-2" alt="Back" />
          {{ t("common.back") }}
        </v-btn>
        <v-btn
          v-if="currentVariableIndex < availableVariables.length - 1"
          @click="handleSaveVariable"
          :class="['forward-button', { 'empty-state': isVariableEmpty }]"
          :variant="isVariableEmpty ? 'outlined' : 'flat'"
          :color="isVariableEmpty ? 'grey-lighten-1' : 'primary'"
          :disabled="isLoading"
          :loading="isLoading"
        >
          {{ t("common.forward") }}
          <img width="7" src="/src/assets/icons/arrow-back-white.svg" class="ml-2" alt="Forward" />
        </v-btn>
        <v-btn
          v-if="currentVariableIndex === availableVariables.length - 1"
          @click="handleSaveVariable"
          :class="['forward-button', { 'empty-state': isVariableEmpty }]"
          :variant="isVariableEmpty ? 'outlined' : 'flat'"
          :color="isVariableEmpty ? 'grey-lighten-1' : 'primary'"
          :disabled="isLoading"
          :loading="isLoading"
        >
          {{ t("common.save") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.max-options-label {
  font-size: 0.8rem;
  color: var(--eerie-black);
  margin-top: 10px;
  color: rgb(176, 0, 32);
}

:deep(.v-card-item) {
  padding: 0;
  margin-top: 20px;
}
:deep(.v-card-text) {
  padding: 0;
  margin-top: 5px;
  text-align: left;
}
.v-card {
  border: none;
  text-align: left;
}

/* Custom date picker styling */
:deep(.v-date-picker) {
  width: 280px !important;
  min-width: 280px !important;

  .v-date-picker__header {
    padding: 4px !important;
  }

  .v-date-picker-month {
    padding: 0 12px 12px !important;
  }

  .v-date-picker-month__day {
    width: 32px !important;
    height: 32px !important;

    .v-btn {
      width: 28px !important;
      height: 28px !important;
      font-size: 0.875rem !important;

      &.v-btn--active {
        background-color: #3B82F6 !important;
        color: white !important;
      }

      &:hover:not(.v-btn--active) {
        background-color: rgba(59, 130, 246, 0.1) !important;
      }
    }

    // Today's date highlight - change from green to blue
    .v-btn--border {
      border-color: #3B82F6 !important;
    }
  }

  .v-date-picker-month__weekday {
    font-size: 0.75rem !important;
    height: 28px !important;
  }

  // Override any primary/green colors to blue
  .bg-primary,
  .text-primary {
    background-color: #3B82F6 !important;
    color: #3B82F6 !important;
  }
  
  .v-date-picker-controls {
    padding: 4px 8px !important;
    
    .v-btn {
      font-size: 0.875rem !important;
    }
  }
  
  .v-date-picker-header__content {
    font-size: 0.875rem !important;
  }
}
.variable-inputs-container {
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-height: 50%;
  padding: 10px;
  transition: 0.3s ease-in-out;
  overflow: hidden;

  // Zaokrąglone pola tekstowe
  :deep(.v-text-field .v-field) {
    border-radius: 4px !important;
  }

  // Zaokrąglone przyciski
  :deep(.v-btn) {
    border-radius: 100px !important;
  }

  @media (min-width: 768px) {
    padding: 10px;
    display: flex;
    gap: 16px;
  }

  .input-label {
    font-size: 1rem;
    color: var(--eerie-black);
    text-align: left;
    margin-top: 4px;
  }

  :is(h3) {
    font-size: 1.125rem;
    font-weight: 600;
    text-align: left;
    margin-bottom: 2px;
  }

  .progress-bar {
    display: block;
    justify-content: space-evenly;
    align-items: center;

    @media (min-width: 768px) {
      display: flex;
    }

    span {
      font-size: 0.688rem;
      color: var(--eerie-black);
      margin-right: 20px;
    }

    // Add shimmer animation to progress bar
    :deep(.v-progress-linear__determinate) {
      background: linear-gradient(90deg, #3B82F6 0%, #60A5FA 50%, #3B82F6 100%);
      background-size: 200% 100%;
      animation: shimmer 2s ease-in-out infinite;
      transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .wizard-buttons {
    display: flex;
    justify-content: end;
    gap: 16px;

    @media (max-width: 768px) {
      gap: 8px;
      margin-bottom: 42px;
      margin-left: -5px;
      margin-right: -5px;
    }

    .forward-button:not(.empty-state) {
      background-color: var(--blue-bonnet) !important;
      color: white !important;
    }

    .forward-button.empty-state {
      background-color: var(--blue-bonnet) !important;
      color: rgba(255, 255, 255, 0.8) !important;
      cursor: not-allowed !important;
      opacity: 0.4 !important;

      &:hover {
        background-color: var(--blue-bonnet) !important;
        box-shadow: none !important;
        opacity: 0.4 !important;
      }
    }

    .forward-button:disabled {
      color: var(--dark-grey) !important;
      background-color: white !important;

      img {
        opacity: 0.5;
      }

      :deep(.v-btn__overlay) {
        background-color: white !important;
      }
    }

    .forward-button,
    .back-button {
      text-decoration: none;
      color: #858788;
      font-size: 0.8rem;
      border-radius: 100px;

      @media (max-width: 768px) {
        margin-top: 2px;
        min-width: 14px !important;
        max-width: 14px !important;
        min-height: 14px !important;
        max-height: 12px !important;
        padding: 0 !important;
      }

      img {
        width: 5px;
      }

      &:hover {
        color: black;
      }
    }

    .forward-button {
      img {
        transform: rotate(180deg);
      }
    }
  }
}

.mobile-navigation-buttons {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin: 2rem 1rem;
  padding-bottom: 20px;

  @media (max-width: 1024px) and (min-width: 600px) {
    // Tablets specifically (600px to 1024px)
    position: fixed !important;
    bottom: 100px !important;
    left: 0 !important;
    right: 0 !important;
    padding: 15px 20px !important;
    background: white !important;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15) !important;
    z-index: 200 !important;
    margin: 0 !important;
    border-top: 2px solid #E2E8F0;
  }

  .mobile-nav-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    padding: 0.875rem 1.25rem;
    border-radius: 100px;
    text-transform: none;

    &.primary {
      background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);

      .v-icon {
        color: white !important;
      }

      &:disabled {
        background: #E5E7EB !important;
        color: #9CA3AF !important;
        box-shadow: none;

        .v-icon {
          color: #9CA3AF !important;
        }
      }
    }

    &.secondary {
      background: white !important;
      color: #475569 !important;
      border: 2px solid #E2E8F0 !important;

      .v-icon {
        color: #475569 !important;
      }

      &:disabled {
        background: #F9FAFB !important;
        color: #D1D5DB !important;
        border-color: #E5E7EB !important;

        .v-icon {
          color: #D1D5DB !important;
        }
      }
    }
  }
}

.wizard-progress-mobile {
  width: 100%;
  padding: 0 1rem;
  margin-bottom: 1.5rem;

  .v-progress-linear {
    border-radius: 9px;
  }
}
</style>
